import React from "react";
import { Eye, FileWarning, ShieldCheck, Sun, Moon, Presentation } from "lucide-react";
import { Switch } from "./ui/switch";
import { useMode } from "../context/ModeContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { FontSizeToggle } from "./FontSizeToggle";

export function CommandBar({ confidentialNotice, onPresent }) {
  const { isInternal, setMode } = useMode();
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  // Founder session AND explicit ?internal=1 URL flag is required to see internal-review controls.
  // Investors (magic-link session only) never see this toggle.
  const showAdminControls = !!user && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("internal") === "1";

  return (
    <div
      data-testid="command-bar"
      className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-line bg-ink/90 backdrop-blur-md px-6 py-2.5 lg:px-14"
    >
      <div className="flex items-center gap-2 font-mono-data text-xs uppercase tracking-wider text-ftext3 truncate">
        <ShieldCheck className="w-3.5 h-3.5 shrink-0 text-ftext3" />
        <span className="truncate">{confidentialNotice}</span>
      </div>

      <div className="flex items-center gap-4 shrink-0">
        <button
          data-testid="present-btn"
          onClick={onPresent}
          className="flex items-center gap-1.5 border border-line2 px-3 h-8 font-mono-data text-xs uppercase tracking-wider text-ftext2 hover:text-sky hover:border-sky transition-colors"
        >
          <Presentation className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Present</span>
        </button>
        {showAdminControls && (
          <div className="flex items-center gap-3" data-testid="admin-mode-controls">
            <span
              className={`hidden sm:flex items-center gap-1.5 font-mono-data text-xs uppercase tracking-wider ${
                isInternal ? "text-[#F59E0B]" : "text-sky"
              }`}
            >
              {isInternal ? <FileWarning className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {isInternal ? "Internal Review" : "Investor Mode"}
            </span>
            <Switch
              data-testid="mode-toggle"
              checked={isInternal}
              onCheckedChange={(v) => setMode(v ? "internal" : "investor")}
              className="data-[state=checked]:bg-[#F59E0B] data-[state=unchecked]:bg-line2"
            />
          </div>
        )}
        <FontSizeToggle />
        <button
          data-testid="theme-toggle"
          onClick={toggle}
          aria-label="Toggle theme"
          className="flex items-center justify-center w-8 h-8 border border-line2 text-ftext2 hover:text-sky hover:border-sky transition-colors"
        >
          {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
